import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Search, CheckCircle } from "lucide-react";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { useAdminAuth } from "../../../context/AdminAuthContext";

// bookingType: "service" | "package"
const AssignStaffModal = ({
  isOpen,
  onClose,
  booking,
  onSuccess,
  branchId: parentBranchId,
  bookingType = "service",
}) => {
  const { admin, isAdmin, isManager } = useAdminAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffMembers, setStaffMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const resolvedBranchId = isAdmin
    ? parentBranchId
    : isManager
      ? admin?.assignedBranch?._id
      : null;

  useEffect(() => {
    if (booking?.staffId) {
      setSelectedStaff({
        id: booking.staffId._id,
        name: booking.staffId.name,
        designation: booking.staffId.designation,
        avatar: booking.staffId.name?.[0]?.toUpperCase(),
      });
    }
  }, [booking]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchStaff = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/admins/get-all-staff`,
          {
            params: resolvedBranchId ? { branchId: resolvedBranchId } : undefined,
          },
        );
        const staffs =
          res.data?.data?.map((u) => ({
            id: u._id,
            name: u.name,
            designation: u.designation,
            avatar: u.name?.[0]?.toUpperCase(),
          })) || [];
        setStaffMembers(staffs);
      } catch (err) {
        console.error(err);
        showError("Failed to fetch staff");
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, [isOpen, resolvedBranchId]);

  const handleAssign = async () => {
    if (!selectedStaff || !booking?._id) return;

    if (booking?.staffId?._id === selectedStaff.id) {
      showError("This staff is already assigned");
      return;
    }

    // ── Pick the right endpoint based on bookingType ──
    let url;
    if (bookingType === "package") {
      url = booking?.staffId?._id
        ? "/package-bookings/staff/replace"
        : "/package-bookings/staff/assign";
    } else {
      url = booking?.staffId?._id
        ? "/staff-assignment/replace"
        : "/staff-assignment/assign";
    }

    setAssigning(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}${url}`, {
        bookingId: booking._id,
        staffId: selectedStaff.id,
      });

      showSuccess("Staff assigned successfully");
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.message || "Failed to assign staff");
    } finally {
      setAssigning(false);
    }
  };

  const filteredStaff = staffMembers.filter((s) =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b bg-[#EDB5A4] rounded-t-lg">
          <h2 className="text-base font-bold">
            {booking?.staffId ? "Replace Staff" : "Assign Staff"}
          </h2>
          <button onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search staff…"
              className="w-full pl-9 pr-3 py-2 border outline-none rounded-md text-sm focus:ring-1 focus:ring-accentTextColor"
            />
          </div>

          <div className="min-h-60 max-h-80 overflow-y-auto space-y-2">
            {loading ? (
              <p className="text-center text-sm text-gray-500 py-6">Loading staff…</p>
            ) : filteredStaff.length === 0 ? (
              <p className="text-center text-sm text-gray-500 py-6">No staff found</p>
            ) : (
              filteredStaff.map((staff) => (
                <div
                  key={staff.id}
                  onClick={() => setSelectedStaff(staff)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer box-border
                    ${selectedStaff?.id === staff.id
                      ? "bg-brandAccentColor/50 border border-accentTextColor"
                      : "hover:bg-gray-100"
                    }`}
                >
                  <div className="w-9 h-9 rounded-full bg-[#EDB5A4] flex items-center justify-center font-bold text-sm">
                    {staff.avatar}
                  </div>
                  <div className="flex-1 leading-tight">
                    <p className="text-sm font-medium">{staff.name}</p>
                    <p className="text-xs text-gray-500">{staff.designation}</p>
                  </div>
                  {selectedStaff?.id === staff.id && (
                    <CheckCircle size={18} className="text-accentTextColor" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t">
          <button onClick={onClose} className="flex-1 py-2 border rounded-md text-sm">
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedStaff || assigning}
            className="flex-1 py-2 bg-primaryBtnColor text-white text-sm font-medium rounded-md disabled:opacity-50"
          >
            {assigning ? "Assigning..." : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignStaffModal;







// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { X, Search, CheckCircle } from "lucide-react";
// import { showError, showSuccess } from "../../../utils/toastUtils";
// import { useAdminAuth } from "../../../context/AdminAuthContext";

// const AssignStaffModal = ({
//   isOpen,
//   onClose,
//   booking,
//   onSuccess,
//   branchId: parentBranchId,
// }) => {
//   const { admin, isAdmin, isManager } = useAdminAuth();

//   const [searchQuery, setSearchQuery] = useState("");
//   const [selectedStaff, setSelectedStaff] = useState(null);
//   const [staffMembers, setStaffMembers] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [assigning, setAssigning] = useState(false);

//   const resolvedBranchId = isAdmin
//     ? parentBranchId
//     : isManager
//       ? admin?.assignedBranch?._id
//       : null;

//   useEffect(() => {
//     if (booking?.staffId) {
//       setSelectedStaff({
//         id: booking.staffId._id,
//         name: booking.staffId.name,
//         designation: booking.staffId.designation,
//         avatar: booking.staffId.name?.[0]?.toUpperCase(),
//       });
//     }
//   }, [booking]);

//   useEffect(() => {
//     if (!isOpen) return;

//     const fetchStaff = async () => {
//       try {
//         setLoading(true);

//         const res = await axios.get(
//           `${process.env.REACT_APP_API_URL}/admins/get-all-staff`,
//           {
//             params: resolvedBranchId
//               ? { branchId: resolvedBranchId }
//               : undefined,
//           },
//         );

//         const staffs =
//           res.data?.data?.map((u) => ({
//             id: u._id,
//             name: u.name,
//             designation: u.designation,
//             avatar: u.name?.[0]?.toUpperCase(),
//           })) || [];

//         setStaffMembers(staffs);
//       } catch (err) {
//         console.error(err);
//         showError("Failed to fetch staff");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchStaff();
//   }, [isOpen, resolvedBranchId]);

//   // ------------------------------------
//   // Assign / Replace staff
//   // ------------------------------------
//   const handleAssign = async () => {
//     if (!selectedStaff || !booking?._id) return;

//     if (booking?.staffId?._id === selectedStaff.id) {
//       showError("This staff is already assigned");
//       return;
//     }

//     const url = booking?.staffId?._id
//       ? "/staff-assignment/replace"
//       : "/staff-assignment/assign";

//     setAssigning(true);
//     try {
//       await axios.post(`${process.env.REACT_APP_API_URL}${url}`, {
//         bookingId: booking._id,
//         staffId: selectedStaff.id,
//       });

//       showSuccess("Staff assigned successfully");
//       onSuccess?.();
//       onClose();
//     } catch (err) {
//       console.error(err);
//       showError(err.response?.data.message || "Failed to assign staff");
//     } finally {
//       setAssigning(false);
//     }
//   };

//   const filteredStaff = staffMembers.filter((s) =>
//     s.name.toLowerCase().includes(searchQuery.toLowerCase()),
//   );

//   if (!isOpen) return null;

//   return (
//     <div
//       onClick={onClose}
//       className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
//     >
//       <div
//         onClick={(e) => e.stopPropagation()}
//         className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
//       >
//         {/* Header */}
//         <div className="flex items-center justify-between p-3 border-b bg-[#EDB5A4] rounded-t-lg">
//           <h2 className="text-base font-bold">
//             {booking?.staffId ? "Replace Staff" : "Assign Staff"}
//           </h2>
//           <button onClick={onClose}>
//             <X size={20} />
//           </button>
//         </div>

//         {/* Body */}
//         <div className="p-4">
//           {/* Search */}
//           <div className="relative mb-3">
//             <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4" />
//             <input
//               value={searchQuery}
//               onChange={(e) => setSearchQuery(e.target.value)}
//               placeholder="Search staff…"
//               className="w-full pl-9 pr-3 py-2 border outline-none rounded-md text-sm focus:ring-1 focus:ring-accentTextColor"
//             />
//           </div>

//           {/* List */}
//           <div className="min-h-60 max-h-80 overflow-y-auto space-y-2">
//             {loading ? (
//               <p className="text-center text-sm text-gray-500 py-6">
//                 Loading staff…
//               </p>
//             ) : filteredStaff.length === 0 ? (
//               <p className="text-center text-sm text-gray-500 py-6">
//                 No staff found
//               </p>
//             ) : (
//               filteredStaff.map((staff) => (
//                 <div
//                   key={staff.id}
//                   onClick={() => setSelectedStaff(staff)}
//                   className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer box-border
//                     ${selectedStaff?.id === staff.id
//                       ? "bg-brandAccentColor/50 border border-accentTextColor"
//                       : "hover:bg-gray-100"
//                     }`}
//                 >
//                   <div className="w-9 h-9 rounded-full bg-[#EDB5A4] flex items-center justify-center font-bold text-sm">
//                     {staff.avatar}
//                   </div>

//                   <div className="flex-1 leading-tight">
//                     <p className="text-sm font-medium">{staff.name}</p>
//                     <p className="text-xs text-gray-500">{staff.designation}</p>
//                   </div>

//                   {selectedStaff?.id === staff.id && (
//                     <CheckCircle size={18} className="text-accentTextColor" />
//                   )}
//                 </div>
//               ))
//             )}
//           </div>
//         </div>

//         {/* Footer */}
//         <div className="flex gap-2 p-4 border-t">
//           <button
//             onClick={onClose}
//             className="flex-1 py-2 border rounded-md text-sm"
//           >
//             Cancel
//           </button>
//           <button
//             onClick={handleAssign}
//             disabled={!selectedStaff || assigning}
//             className="flex-1 py-2 bg-primaryBtnColor text-white text-sm font-medium rounded-md disabled:opacity-50"
//           >
//             {assigning ? "Assigning..." : "Assign"}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AssignStaffModal;

// // import React, { useEffect, useState } from "react";
// // import axios from "axios";
// // import { X, Search, CheckCircle } from "lucide-react";
// // import { showError, showSuccess } from "../../../utils/toastUtils";

// // const AssignStaffModal = ({ isOpen, onClose, booking, onSuccess }) => {
// //   const [searchQuery, setSearchQuery] = useState("");
// //   const [selectedStaff, setSelectedStaff] = useState(null);
// //   const [staffMembers, setStaffMembers] = useState([]);
// //   const [loading, setLoading] = useState(false);
// //   const [assigning, setAssigning] = useState(false);

// //   useEffect(() => {
// //     if (booking?.staffId) {
// //       setSelectedStaff({
// //         id: booking.staffId._id,
// //         name: booking.staffId.name,
// //         designation: booking.staffId.designation,
// //         avatar: booking.staffId.name?.[0]?.toUpperCase(),
// //       });
// //     }
// //   }, [booking]);

// //   useEffect(() => {
// //     if (!isOpen) return;

// //     const fetchStaff = async () => {
// //       try {
// //         setLoading(true);
// //         const res = await axios.get(
// //           `${process.env.REACT_APP_API_URL}/admins/get-all`
// //         );

// //         const staffs = res.data?.data
// //           ?.filter((u) => u.userType === "staff")
// //           ?.map((u) => ({
// //             id: u._id,
// //             name: u.name,
// //             designation: u.designation,
// //             avatar: u.name?.[0]?.toUpperCase(),
// //           }));

// //         setStaffMembers(staffs || []);
// //       } catch (err) {
// //         console.error(err);
// //         showError("Failed to fetch staff");
// //       } finally {
// //         setLoading(false);
// //       }
// //     };

// //     fetchStaff();
// //   }, [isOpen]);

// //   const handleAssign = async () => {
// //     if (!selectedStaff || !booking?._id) return;

// //     if (booking?.staffId?._id === selectedStaff.id) {
// //       showError("This staff is already assigned");
// //       return;
// //     }

// //     let url;
// //     if (booking?.staffId?._id) {
// //       url = `/staff-assignment/replace`;
// //     } else {
// //       url = `/staff-assignment/assign`;
// //     }

// //     setAssigning(true);
// //     try {
// //       await axios.post(`${process.env.REACT_APP_API_URL}${url}`, {
// //         bookingId: booking?._id,
// //         staffId: selectedStaff.id,
// //       });

// //       showSuccess("Staff assigned successfully");
// //       onSuccess?.();
// //       onClose();
// //     } catch (err) {
// //       console.error(err);
// //       showError("Failed to assign staff");
// //     } finally {
// //       setAssigning(false);
// //     }
// //   };

// //   const filteredStaff = staffMembers.filter((s) =>
// //     s.name.toLowerCase().includes(searchQuery.toLowerCase())
// //   );

// //   if (!isOpen) return null;

// //   return (
// //     <div
// //       onClick={onClose}
// //       className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
// //     >
// //       <div
// //         onClick={(e) => e.stopPropagation()}
// //         className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
// //       >
// //         {/* Header */}
// //         <div className="flex items-center justify-between p-3 border-b bg-[#EDB5A4] rounded-t-lg">
// //           <h2 className="text-base font-bold">
// //             {booking?.staffId ? "Replace Staff" : "Assign Staff"}
// //           </h2>
// //           <button onClick={onClose}>
// //             <X size={20} />
// //           </button>
// //         </div>

// //         {/* Body */}
// //         <div className="p-4">
// //           {/* Search */}
// //           <div className="relative mb-3">
// //             <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4" />
// //             <input
// //               value={searchQuery}
// //               onChange={(e) => setSearchQuery(e.target.value)}
// //               placeholder="Search staff…"
// //               className="w-full pl-9 pr-3 py-2 border outline-none rounded-md text-sm focus:ring-1 focus:ring-accentTextColor"
// //             />
// //           </div>

// //           {/* List */}
// //           <div className="min-h-60 max-h-80 overflow-y-auto space-y-2">
// //             {loading ? (
// //               <p className="text-center text-sm text-gray-500 py-6">
// //                 Loading staff…
// //               </p>
// //             ) : filteredStaff.length === 0 ? (
// //               <p className="text-center text-sm text-gray-500 py-6">
// //                 No staff found
// //               </p>
// //             ) : (
// //               filteredStaff.map((staff) => (
// //                 <div
// //                   key={staff.id}
// //                   onClick={() => setSelectedStaff(staff)}
// //                   className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer box-border
// //                     ${
// //                       selectedStaff?.id === staff.id
// //                         ? "bg-brandAccentColor/50 border border-accentTextColor"
// //                         : "hover:bg-gray-100"
// //                     }`}
// //                 >
// //                   <div className="w-9 h-9 rounded-full bg-[#EDB5A4] flex items-center justify-center font-bold text-sm">
// //                     {staff.avatar}
// //                   </div>

// //                   <div className="flex-1 leading-tight">
// //                     <p className="text-sm font-medium">{staff.name}</p>
// //                     <p className="text-xs text-gray-500">{staff.designation}</p>
// //                   </div>

// //                   {selectedStaff?.id === staff.id && (
// //                     <CheckCircle size={18} className="text-accentTextColor" />
// //                   )}
// //                 </div>
// //               ))
// //             )}
// //           </div>
// //         </div>

// //         {/* Footer */}
// //         <div className="flex gap-2 p-4 border-t">
// //           <button
// //             onClick={onClose}
// //             className="flex-1 py-2 border rounded-md text-sm"
// //           >
// //             Cancel
// //           </button>
// //           <button
// //             onClick={handleAssign}
// //             disabled={!selectedStaff || assigning}
// //             className="flex-1 py-2 bg-primaryBtnColor text-white text-sm font-medium rounded-md disabled:opacity-50"
// //           >
// //             {assigning ? "Assigning..." : "Assign"}
// //           </button>
// //         </div>
// //       </div>
// //     </div>
// //   );
// // };

// // export default AssignStaffModal;
